/**
 * Aspect — Wall cutaway (smooth transparency in overhead / scene-view mode)
 * --------------------------------------------------------------
 * When overhead / scene-view mode is active, walls and doors render at 50%
 * opacity and the ceiling at 20% (80% transparent). First-person restores solids.
 */

import * as pc from "playcanvas";

const OVERHEAD_WALL_OPACITY = 0.5;
/** Ceiling/roof: 80% transparent (20% opaque) in overhead mode. */
const OVERHEAD_CEILING_OPACITY = 0.2;
const LERP_SPEED = 7.0;
const OUTLINE_THICKNESS = 0.025;

function applyMaterialOpacity(mat, opacity) {
  if (!mat) return;
  mat.opacity = opacity;
  if (opacity < 0.995) {
    mat.blendType = pc.BLEND_NORMAL;
    mat.depthWrite = false;
  } else {
    mat.blendType = pc.BLEND_NONE;
    mat.depthWrite = true;
  }
  mat.update();
}

export class WallCutaway {
  /**
   * @param {pc.AppBase} app
   * @param {pc.Entity} camera
   * @param {import('../scene_manager.js').SceneManager} scene
   * @param {{ enabled?: () => boolean }} [opts]
   */
  constructor(app, camera, scene, opts = {}) {
    this.app = app;
    this.camera = camera;
    this.scene = scene;
    this._isActive = opts.enabled ?? (() => false);

    /** @type {pc.StandardMaterial[]} */
    this._materials = [];
    /** @type {pc.StandardMaterial[]} persistent extras (e.g. door panels) */
    this._extraMats = [];
    /** @type {pc.Entity | null} */
    this._ceilingEntity = null;
    /** @type {pc.StandardMaterial | null} */
    this._ceilingMat = null;
    /** @type {pc.Entity | null} */
    this._outlineRoot = null;
    /** @type {pc.StandardMaterial | null} */
    this._outlineMat = null;
    this._wallOpacity = 1;
    this._ceilingOpacity = 1;

    this.app.on("scene:room-loaded", () => this.refresh());
    // Single hook for every incremental geometry edit (wall rebuilds, opening
    // patches, envelope refreshes, clear-apartment) — no manual re-call sites.
    this.app.on("scene:geometry-changed", () => this.refresh());
    this.app.on("update", (dt) => this._update(dt));
  }

  /** Re-collect wall materials and rebuild the ceiling outline after room edits. */
  refresh() {
    this._wallOpacity = 1;
    this._ceilingOpacity = 1;
    this._collectMaterials();
    for (const mat of this._materials) applyMaterialOpacity(mat, 1);
    if (this._ceilingMat) applyMaterialOpacity(this._ceilingMat, 1);
    this._buildCeilingOutline();
    this._syncOverheadVisuals(this._isActive());
  }

  _floorOnly() {
    return !(this.scene?.room?.walls?.length);
  }

  /** Track wall + door materials for the current room (ceiling handled separately). */
  _collectMaterials() {
    this._materials = [];
    const wallMat = this.scene.roomRoot?._aspectWallMat;
    if (wallMat) this._materials.push(wallMat);

    this._ceilingMat = this.scene.roomRoot?._aspectCeilingMat ?? null;
    this._ceilingEntity = null;
    for (const child of this.scene.roomRoot?.children ?? []) {
      if (child._aspectKind === "ceiling") {
        this._ceilingEntity = child;
        break;
      }
    }

    for (const mat of this._extraMats) {
      if (mat) this._materials.push(mat);
    }
  }

  /** Register an extra material (e.g. door panels) to fade with walls. */
  registerMaterial(mat) {
    if (mat && !this._extraMats.includes(mat)) this._extraMats.push(mat);
    if (mat && !this._materials.includes(mat)) this._materials.push(mat);
  }

  _ensureOutlineMat() {
    if (this._outlineMat) return;
    const mat = new pc.StandardMaterial();
    mat.diffuse = new pc.Color(0.92, 0.94, 0.98);
    mat.emissive = new pc.Color(0.55, 0.58, 0.65);
    mat.emissiveIntensity = 0.35;
    mat.useMetalness = false;
    mat.gloss = 0.1;
    mat.depthWrite = false;
    mat.blendType = pc.BLEND_NORMAL;
    mat.opacity = 0.9;
    mat.update();
    this._outlineMat = mat;
  }

  _destroyCeilingOutline() {
    this._outlineRoot?.destroy();
    this._outlineRoot = null;
  }

  /** Thin rectangular loop at ceiling height — visible only in overhead mode. */
  _buildCeilingOutline() {
    this._destroyCeilingOutline();
    const room = this.scene.room;
    const b = room?.bounds;
    if (!b || !this.scene.roomRoot) return;

    this._ensureOutlineMat();
    const w = b.max[0] - b.min[0];
    const d = b.max[2] - b.min[2];
    const floorY = room.floorY ?? 0;
    const ceilH = room.dimensions?.height ?? 2.8;
    const y = floorY + ceilH;
    const cx = (b.min[0] + b.max[0]) / 2;
    const cz = (b.min[2] + b.max[2]) / 2;
    const t = OUTLINE_THICKNESS;

    const root = new pc.Entity("ceiling-outline");
    const bar = (sx, sy, sz, px, py, pz) => {
      const e = new pc.Entity("bar");
      e.addComponent("render", { type: "box", material: this._outlineMat, castShadows: false, receiveShadows: false });
      e.setLocalScale(sx, sy, sz);
      e.setLocalPosition(px, py, pz);
      root.addChild(e);
    };

    bar(w + t, t, t, 0, 0, -d / 2);
    bar(w + t, t, t, 0, 0, d / 2);
    bar(t, t, d + t, -w / 2, 0, 0);
    bar(t, t, d + t, w / 2, 0, 0);

    root.setLocalPosition(cx, y, cz);
    root.enabled = false;
    this.scene.roomRoot.addChild(root);
    this._outlineRoot = root;
  }

  _syncOverheadVisuals(active) {
    const floorOnly = this._floorOnly();
    if (this._ceilingEntity?.render) {
      this._ceilingEntity.render.enabled = !floorOnly;
    }
    if (this._outlineRoot) {
      this._outlineRoot.enabled = false;
    }
    if (!active && this._ceilingMat && !floorOnly) {
      applyMaterialOpacity(this._ceilingMat, 1);
    }
  }

  _syncCeilingState(active) {
    this._syncOverheadVisuals(active);
  }

  _update(dt) {
    const active = this._isActive();
    this._syncOverheadVisuals(active);

    const wallTarget = active ? OVERHEAD_WALL_OPACITY : 1;
    const ceilTarget = active ? OVERHEAD_CEILING_OPACITY : 1;
    const k = 1 - Math.exp(-LERP_SPEED * dt);
    this._wallOpacity += (wallTarget - this._wallOpacity) * k;
    this._ceilingOpacity += (ceilTarget - this._ceilingOpacity) * k;

    if (!active && Math.abs(this._wallOpacity - 1) < 0.002) this._wallOpacity = 1;
    if (!active && Math.abs(this._ceilingOpacity - 1) < 0.002) this._ceilingOpacity = 1;

    for (const mat of this._materials) applyMaterialOpacity(mat, this._wallOpacity);
    if (this._ceilingMat && !this._floorOnly()) applyMaterialOpacity(this._ceilingMat, this._ceilingOpacity);
  }

  destroy() {
    this._destroyCeilingOutline();
    this._outlineMat?.destroy();
    this._outlineMat = null;
  }
}

export default WallCutaway;
